package com.oasisfeng.invaluable.meaning

import com.oasisfeng.invaluable.Dimensions
import com.oasisfeng.invaluable.NestedLink
import com.oasisfeng.invaluable.actor.Actor
import com.oasisfeng.invaluable.data.LinkTopology
import com.oasisfeng.invaluable.data.LinkView
import com.oasisfeng.invaluable.then
import kotlin.jvm.JvmInline

/**
 * The profile anchor for one [Actor], a self-referential `actor -> MEANING -> actor`.
 *
 * This sits deliberately one level below the actor's `actor -> MEANING` content origin (where ordinary posts live), so
 * that profile *labels* — a display name and any other public appearances — are structurally distinct from content and
 * never read as feed posts. Direct children of this anchor are label links: any appearance (name, handle, bio, …)
 * related to this actor, postable and backable by anyone. A reader picks the most suitable label from the commitments
 * (e.g. the highest-backed text label as the display name). Commitment issuers, not this anchor, express who published
 * or backed each label.
 */
@JvmInline value class ActorProfile private constructor(override val link: NestedLink) : LinkView {

    companion object : LinkTopology<ActorProfile>(::ActorProfile) {
        /** Enter [actor]'s profile space: the self-referential anchor `actor -> MEANING -> actor`. */
        operator fun invoke(actor: Actor): ActorProfile = ActorProfile(actor.link.then(Dimensions.MEANING()).then(actor.link))
        /** `(<Actor> -> MEANING) -> <the same Actor>`: target is the subject actor; its parent is that actor's origin. */
        override fun NestedLink.matchesTopology(): Boolean {
            val subject = nestedTargetOrNull()?.takeIf(Actor::matchNode) ?: return false
            val origin = parent().nestedOrNull() ?: return false
            return origin.target() == Dimensions.MEANING() && origin.parent().nestedOrNull() == subject
        }
    }

    /** Subject actor whose profile this anchors. */
    fun actor(): Actor = Actor.render(link.requireNestedTarget()) ?: error("Actor profile target is not an Actor.")

    override fun toString(): String = link.toString()
}
